import { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
} from "reactstrap";

const FREQUENCY_OPTIONS = [
  { value: "DAILY", label: "Diariamente" },
  { value: "WEEKLY", label: "Semanal" },
  { value: "MONTHLY", label: "Mensal" },
];

const SEARCH_PATTERNS = [
  { value: "d-1", label: "d-1 (um dia)" },
  { value: "d-3", label: "d-3 (três dias)" },
  { value: "d-7", label: "d-7 (uma semana)" },
  { value: "d-14", label: "d-14 (duas semanas)" },
  { value: "d-30", label: "d-30 (trinta dias)" },
];

const WEEKDAYS = [
  { value: 0, label: "D", fullLabel: "Domingo" },
  { value: 1, label: "S", fullLabel: "Segunda" },
  { value: 2, label: "T", fullLabel: "Terça" },
  { value: 3, label: "Q", fullLabel: "Quarta" },
  { value: 4, label: "Q", fullLabel: "Quinta" },
  { value: 5, label: "S", fullLabel: "Sexta" },
  { value: 6, label: "S", fullLabel: "Sábado" },
];

// Converts UTC hour/minute (stored in backend) to a local "HH:mm" string for the <input type="time">.
function utcToLocalTime(hour, minute) {
  const utcDate = new Date();
  utcDate.setUTCHours(hour ?? 9, minute ?? 0, 0, 0);
  const h = String(utcDate.getHours()).padStart(2, "0");
  const m = String(utcDate.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}

// Converts a local "HH:mm" string back to UTC hour/minute for sending to the backend.
function localTimeToUtc(time) {
  const [hourStr, minuteStr] = (time || "09:00").split(":");
  const localDate = new Date();
  localDate.setHours(parseInt(hourStr, 10), parseInt(minuteStr, 10), 0, 0);
  return {
    hour: localDate.getUTCHours(),
    minute: localDate.getUTCMinutes(),
  };
}

const ModalEditRecurrenceSchedule = ({
  isOpen,
  toggle,
  recurrenceInfo,
  onSave,
}) => {
  const [frequency, setFrequency] = useState("DAILY");
  const [executionTime, setExecutionTime] = useState("09:00");
  const [searchPattern, setSearchPattern] = useState("d-1");
  const [selectedDays, setSelectedDays] = useState([1]);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen && recurrenceInfo) {
      setFrequency(recurrenceInfo.frequency || "DAILY");
      setExecutionTime(
        utcToLocalTime(recurrenceInfo.hour, recurrenceInfo.minute),
      );
      setSearchPattern(recurrenceInfo.searchPattern || "d-1");
      setSelectedDays(
        recurrenceInfo.daysOfWeek?.length ? recurrenceInfo.daysOfWeek : [1],
      );
      setDayOfMonth(recurrenceInfo.dayOfMonth || 1);
      setError("");
    }
  }, [isOpen, recurrenceInfo]);

  const handleDayToggle = (day) => {
    setSelectedDays((current) => {
      if (current.includes(day)) {
        if (current.length === 1) return current;
        return current.filter((d) => d !== day);
      }
      return [...current, day];
    });
  };

  const validate = () => {
    if (frequency === "WEEKLY" && selectedDays.length === 0) {
      return "Selecione ao menos um dia da semana.";
    }
    if (
      frequency === "MONTHLY" &&
      (!dayOfMonth || dayOfMonth < 1 || dayOfMonth > 31)
    ) {
      return "Informe um dia do mês válido (1-31).";
    }
    return "";
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const { hour, minute } = localTimeToUtc(executionTime);
    const payload = {
      recurrence_frequency: frequency,
      recurrence_hour: hour,
      recurrence_minute: minute,
      recurrence_search_pattern: searchPattern,
      ...(frequency === "WEEKLY" && { recurrence_days_of_week: selectedDays }),
      ...(frequency === "MONTHLY" && {
        recurrence_day_of_month: Number(dayOfMonth),
      }),
    };

    setSaving(true);
    setError("");
    try {
      await onSave(payload);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Erro ao atualizar o agendamento da pipeline.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>Editar agendamento</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="edit-recurrence-frequency">Frequência</Label>
          <Input
            id="edit-recurrence-frequency"
            type="select"
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
          >
            {FREQUENCY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Input>
        </FormGroup>

        {frequency === "WEEKLY" && (
          <FormGroup>
            <Label>Dias da semana</Label>
            <div className="d-flex flex-wrap" style={{ gap: "0.375rem" }}>
              {WEEKDAYS.map((day) => {
                const isActive = selectedDays.includes(day.value);
                return (
                  <button
                    key={day.value}
                    type="button"
                    title={day.fullLabel}
                    onClick={() => handleDayToggle(day.value)}
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      border: `1px solid ${isActive ? "#5e72e4" : "#dee2e6"}`,
                      backgroundColor: isActive ? "#5e72e4" : "#fff",
                      color: isActive ? "#fff" : "#212529",
                      fontWeight: 600,
                      fontSize: "13px",
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>
          </FormGroup>
        )}

        {frequency === "MONTHLY" && (
          <FormGroup>
            <Label for="edit-recurrence-day-of-month">Dia do mês</Label>
            <Input
              id="edit-recurrence-day-of-month"
              type="number"
              min={1}
              max={31}
              value={dayOfMonth}
              onChange={(e) => setDayOfMonth(e.target.value)}
              style={{ maxWidth: "150px" }}
            />
          </FormGroup>
        )}

        <FormGroup>
          <Label for="edit-recurrence-time">Horário de execução</Label>
          <Input
            id="edit-recurrence-time"
            type="time"
            step="900"
            value={executionTime}
            onChange={(e) => setExecutionTime(e.target.value)}
            style={{ maxWidth: "150px" }}
          />
        </FormGroup>

        <FormGroup>
          <Label for="edit-recurrence-search-pattern">Padrão de busca</Label>
          <Input
            id="edit-recurrence-search-pattern"
            type="select"
            value={searchPattern}
            onChange={(e) => setSearchPattern(e.target.value)}
          >
            {SEARCH_PATTERNS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Input>
        </FormGroup>

        {error && <div className="text-danger small">{error}</div>}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={saving}>
          Cancelar
        </Button>
        <Button color="primary" onClick={handleSave} disabled={saving}>
          {saving ? "Salvando..." : "Salvar"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ModalEditRecurrenceSchedule;
